import { createModel } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import {
  FilterLibraryModelType,
  FilterLibraryType,
  PageRequest,
  PaginationType,
  RootModel,
} from '@types';
import { DEFAULT_PAGINATION } from '@commons/constants';
import { Dispatch } from '@redux/store';

const initialState: FilterLibraryModelType = {
  ids: [],
  loading: false,
  editId: null,
  pagination: DEFAULT_PAGINATION,
  criteria: {
    keyword: '',
  },
  paramsSearch: [],
};

export const filterLibraries = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setParamsSearch: produce((state, payload) => {
      state.paramsSearch = payload;
    }),
  },
  effects: (dispatch: Dispatch) => ({
    async reloadList(payload, state): Promise<void> {
      const { criteria, pagination } = state.blogs;
      await this.filterAsync({ ...criteria, ...pagination });
    },

    async filterAsync(payload): Promise<void> {
      this.updateLoading(true);
      try {
        const data: { content: FilterLibraryType[] } & PaginationType =
          await dispatch.filterLibraryEntities.load(payload);
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, pageable, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },

    async pagingAsync(payload: PageRequest, state): Promise<void> {
      const { criteria = {}, pagination = {} } = state.blogs;
      await this.filterAsync({ ...criteria, ...pagination, ...payload });
    },

    async getAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const item = await dispatch.filterLibraryEntities.findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },

    async deleteAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        await dispatch.filterLibraryEntities.delete(payload);
        await this.reloadList();
        message.warning(`Item deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete`);
      } finally {
        this.updateLoading(false);
      }
    },

    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const result = await dispatch.filterLibraryEntities.save(payload);
        message.success('Save successfully');
        return result;
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }
    },

    async getParamsAsync(): Promise<void> {
      this.updateLoading(true);
      try {
        const res = await dispatch.filterLibraryEntities.getParams();
        this.setParamsSearch(res);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get params`);
      } finally {
        this.updateLoading(false);
      }
    },

    async publish(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch.filterLibraryEntities.publish(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.updateLoading(false);
      }
    },

    async saveAndPublishAsync(payload, state) {
      this.updateLoading(true);
      try {
        const result = await dispatch.filterLibraryEntities.save(payload);
        const res = await dispatch.filterLibraryEntities.publish({
          id: result.id,
          published: true,
        });
        message.success('Save and published successfully');
        return res;
      } catch (err) {
        console.error(err);
        message.error('Fail to save and published');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});
